import { useState, useEffect, useCallback } from "react";
import { MODULE_MAP, CLIENT_FIELD_LABELS, MODULE_ACTION_OPTIONS, CRITICAL_ACTIONS } from "@/pages/admin/system/constants";

interface OperationLogsPanelProps {
  fetchOperationLogs: (params?: Record<string, unknown>) => Promise<any>;
  exportOperationLogs: (params?: Record<string, unknown>) => Promise<any>;
  onMessage: (msg: string) => void;
}

export default function OperationLogsPanel({
  fetchOperationLogs,
  exportOperationLogs,
  onMessage,
}: OperationLogsPanelProps) {
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsCount, setLogsCount] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const logsPageSize = 20;
  const [logsActionFilter, setLogsActionFilter] = useState("all");
  const [logsModuleFilter, setLogsModuleFilter] = useState("all");
  const [logsEmployeeFilter, setLogsEmployeeFilter] = useState("all");
  const [logsTimeRange, setLogsTimeRange] = useState("all");
  const [logsEmployees, setLogsEmployees] = useState<string[]>([]);
  const [logsError, setLogsError] = useState("");
  const [logsClients, setLogsClients] = useState<Record<number, { name: string; phone: string }>>({});
  const [logsStages, setLogsStages] = useState<Record<number, string>>({});
  const [logsEmployeeMap, setLogsEmployeeMap] = useState<Record<number, string>>({});
  const [logsRoleMap, setLogsRoleMap] = useState<Record<number, string>>({});
  const [logsExporting, setLogsExporting] = useState(false);

  const loadOperationLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError("");
    const res = await fetchOperationLogs({
      limit: logsPageSize,
      offset: logsPage * logsPageSize,
      log_action: logsActionFilter !== "all" ? logsActionFilter : undefined,
      employee_name: logsEmployeeFilter !== "all" ? logsEmployeeFilter : undefined,
      module: logsModuleFilter !== "all" ? logsModuleFilter : undefined,
      time_range: logsTimeRange !== "all" ? logsTimeRange : undefined,
    } as any);
    setLogsLoading(false);
    if (res?.success) {
      setLogs(res.logs || []);
      setLogsCount(res.count || 0);
      if (res.employees?.length) setLogsEmployees(res.employees);
      if (res.clients) setLogsClients(res.clients);
      if (res.stages) setLogsStages(res.stages);
      if (res.employees_map) setLogsEmployeeMap(res.employees_map);
      if (res.roles_map) setLogsRoleMap(res.roles_map);
    } else {
      setLogsError(res?.error || "加载失败");
      setLogs([]);
      setLogsCount(0);
    }
  }, [fetchOperationLogs, logsPage, logsActionFilter, logsEmployeeFilter, logsModuleFilter, logsTimeRange]);

  useEffect(() => { loadOperationLogs(); }, [loadOperationLogs]);

  const handleExportLogs = async () => {
    setLogsExporting(true);
    try {
      const res = await exportOperationLogs({
        employee_name: logsEmployeeFilter !== "all" ? logsEmployeeFilter : undefined,
        log_action: logsActionFilter !== "all" ? logsActionFilter : undefined,
        module: logsModuleFilter !== "all" ? logsModuleFilter : undefined,
        time_range: logsTimeRange !== "all" ? logsTimeRange : undefined,
      } as any);
      if (res?.success && res.logs) {
        const clients = res.clients || {};
        const stages = res.stages || {};
        const empMap = res.employees_map || {};
        const roleMap = res.roles_map || {};
        const actionLabel: Record<string, string> = {
          login: "登录系统", create_client: "创建客户", edit_client: "编辑客户", delete_client: "删除客户",
          progress_update: "进度更新", assign_advisor: "分配顾问", create_employee: "创建员工", update_employee: "编辑员工",
          delete_employee: "删除员工", create_role: "创建角色", update_role: "编辑角色", delete_role: "删除角色",
          send_notification: "发送通知", upload_file: "文件上传", delete_file: "文件删除",
          change_password: "更改密码",
          create_scheduled_notification: "定时通知", update_scheduled_notification: "定时通知",
          cancel_scheduled_notification: "定时通知", send_scheduled_now: "定时通知",
        };
        const BOM = "\uFEFF";
        const headers = ["序号", "操作员工", "操作模块", "操作名称", "操作时间", "操作详情"];
        const rows = res.logs.map((log: any, idx: number) => {
          const module = MODULE_MAP[log.action] || "其他";
          const action = actionLabel[log.action] || log.action;
          const d = log.details || {};
          const clientId = (log.target_type === "client" || log.target_type === "progress") && log.target_id ? log.target_id : null;
          const clientInfo = clientId ? clients[clientId] : null;
          const storedClientName = d.client_name || "未知";
          const storedClientPhone = d.phone || d.client_phone || "-";
          const displayClientName = clientInfo?.name || storedClientName;
          const displayClientPhone = clientInfo?.phone || storedClientPhone;
          // create_client 优先显示 client_code
          const displayIdentifier = log.action === "create_client"
            ? (d.client_code || storedClientPhone)
            : displayClientPhone;
          const stageId = d.stage_id || null;
          const stageName = d.stage_name || (stageId ? stages[stageId] || "" : "");
          let detail = "";
          if (log.action === "create_client") detail = `新建客户「${displayClientName}」(${displayIdentifier})`;
          else if (log.action === "edit_client") detail = `「${displayClientName}」(${displayClientPhone}) 编辑字段：${Array.isArray(d.updated_fields) ? d.updated_fields.map((f: string) => CLIENT_FIELD_LABELS[f] || f).join("、") : "-"}`;
          else if (log.action === "delete_client") detail = `删除客户「${displayClientName}」(${displayClientPhone})`;
          else if (log.action === "assign_advisor") detail = `「${displayClientName}」(${displayClientPhone}) 分配顾问「${d.advisor_name || "未知"}」`;
          else if (log.action === "progress_update") {
            const parts: string[] = [];
            if (d.fee_updated) parts.push("更新收入数据");
            if (d.expense_updated) parts.push("更新支出数据");
            if (d.notes_updated) parts.push("更新阶段备注");
            if (d.new_status === "completed") parts.push("标记为「已完成」");
            else if (d.new_status === "in_progress") parts.push("标记为「进行中」");
            detail = `「${displayClientName}」(${displayClientPhone}) ${stageName} ${parts.length > 0 ? parts.join("，") : "进度更新"}`;
          } else if (log.action === "upload_file") {
            const bucketLabel = d.bucket === "crm-media" ? "上传影像" : "上传资料";
            if (d.file_names && Array.isArray(d.file_names)) detail = `「${displayClientName}」(${displayClientPhone}) ${stageName} ${bucketLabel}：${d.file_names.join("、")}`;
            else detail = `「${displayClientName}」(${displayClientPhone}) ${stageName} 上传文件「${d.file_name || "未知"}」`;
          } else if (log.action === "delete_file") {
            const bucketLabel = d.bucket === "crm-media" ? "删除影像" : "删除资料";
            detail = `「${displayClientName}」(${displayClientPhone}) ${stageName} ${bucketLabel}「${d.file_name || "未知"}」`;
          } else if (log.action === "login") detail = "登录后台管理系统";
          else if (log.action === "send_notification") detail = `发送通知「${d.title || ""}」给${d.client_count || 0}位客户`;
          else if (log.action === "create_scheduled_notification") {
            const schedAt = d.scheduled_at ? new Date(d.scheduled_at).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
            detail = `创建定时通知「${d.title || ""}」，将于 ${schedAt} 自动发送`;
          } else if (log.action === "update_scheduled_notification") detail = `定时通知「${d.title || ""}」发送时间修改为 ${new Date(d.scheduled_at).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })} 自动发送`;
          else if (log.action === "cancel_scheduled_notification") {
            detail = d.title ? `定时通知「${d.title}」已取消` : `取消定时通知：共${d.count || 0}条通知已取消`;
          } else if (log.action === "send_scheduled_now") {
            const count = d.client_count || d.sent_count || 0;
            detail = d.title ? `定时通知「${d.title}」已发送给${count}位客户` : `定时通知已发送：共${count}条`;
          }
          else if (log.action === "create_employee") detail = `创建员工「${d.name || "未知"}」`;
          else if (log.action === "update_employee") {
            const empName = (log.target_id && empMap[log.target_id]) || d.employee_name || d.name || "";
            detail = empName ? `编辑员工「${empName}」信息` : "编辑员工信息";
          } else if (log.action === "delete_employee") {
            const empName = (log.target_id && empMap[log.target_id]) || d.employee_name || d.name || "";
            const transferCount = d.transferred_clients || 0;
            const transferTo = d.transferred_to || "";
            const clientList: Array<{ name: string }> = d.transferred_client_list || [];
            if (transferCount > 0 && transferTo) {
              const clientNames = clientList.length > 0 ? `（${clientList.map((c) => c.name).join("、")}）` : "";
              detail = empName
                ? `删除员工「${empName}」，共转移 ${transferCount} 位客户至「${transferTo}」名下${clientNames}`
                : `删除员工，共转移 ${transferCount} 位客户至「${transferTo}」名下${clientNames}`;
            } else {
              detail = empName ? `删除员工「${empName}」` : "删除员工";
            }
          } else if (log.action === "create_role") detail = `创建角色「${d.name || "未知"}」`;
          else if (log.action === "update_role") {
            const roleName = (log.target_id && roleMap[log.target_id]) || d.role_name || d.name || "";
            detail = roleName ? `编辑角色「${roleName}」权限` : "编辑角色权限";
          } else if (log.action === "delete_role") {
            const roleName = (log.target_id && roleMap[log.target_id]) || d.role_name || d.name || "";
            detail = roleName ? `删除角色「${roleName}」` : "删除角色";
          } else if (log.action === "change_password") {
            const empName = (log.target_id && empMap[log.target_id]) || log.employee_name || "";
            detail = empName ? `${empName} 更改登录密码` : "更改登录密码";
          }
          const csvRow = [idx + 1, log.employee_name, module, action, new Date(log.created_at).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }), detail];
          const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
          return csvRow.map((cell: any) => esc(String(cell))).join(",");
        });
        const csvContent = BOM + [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `操作日志_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        onMessage(`导出成功！共 ${res.logs.length} 条记录`);
      } else {
        onMessage(res?.error || "导出失败");
      }
    } catch (e: any) {
      onMessage(`导出失败: ${e.message || "未知错误"}`);
    }
    setLogsExporting(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { value: "all", label: "全部" },
            { value: "today", label: "今天" },
            { value: "week", label: "本周" },
            { value: "month", label: "本月" },
          ].map((t) => (
            <button key={t.value} onClick={() => { setLogsTimeRange(t.value); setLogsPage(0); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${logsTimeRange === t.value ? "bg-white text-[#1a3a5c]" : "text-gray-500 hover:text-gray-700"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <select value={logsModuleFilter} onChange={(e) => { setLogsModuleFilter(e.target.value); setLogsActionFilter("all"); setLogsPage(0); }} className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
          <option value="all">全部模块</option>
          <option value="客户管理">客户管理</option>
          <option value="进度管理">进度管理</option>
          <option value="通知推送">通知推送</option>
          <option value="系统管理">系统管理</option>
        </select>
        {logsModuleFilter !== "all" && (
          <select value={logsActionFilter} onChange={(e) => { setLogsActionFilter(e.target.value); setLogsPage(0); }} className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
            <option value="all">全部操作</option>
            {(MODULE_ACTION_OPTIONS[logsModuleFilter] || []).map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        )}
        <select value={logsEmployeeFilter} onChange={(e) => { setLogsEmployeeFilter(e.target.value); setLogsPage(0); }} className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
          <option value="all">全部员工</option>
          {logsEmployees.map((name) => (<option key={name} value={name}>{name}</option>))}
        </select>
        <button onClick={loadOperationLogs} className="px-3 py-2 bg-gray-50 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"><i className="ri-refresh-line"></i>刷新</button>
        <button onClick={handleExportLogs} disabled={logsExporting} className="px-3 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed">
          {logsExporting ? <><i className="ri-loader-4-line animate-spin"></i>导出中...</> : <><i className="ri-download-line"></i>导出CSV</>}
        </button>
        {(logsTimeRange !== "all" || logsModuleFilter !== "all" || logsEmployeeFilter !== "all") && (
          <button onClick={() => { setLogsTimeRange("all"); setLogsModuleFilter("all"); setLogsActionFilter("all"); setLogsEmployeeFilter("all"); setLogsPage(0); }} className="px-3 py-2 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"><i className="ri-close-line"></i>清除筛选</button>
        )}
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full min-w-max text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap w-12">序号</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">操作员工</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">操作模块</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">操作时间</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap">操作详情</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logsLoading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
              ) : logsError ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center"><div className="text-red-400"><i className="ri-error-warning-line text-2xl mb-2 block"></i><p className="text-sm">{logsError}</p><p className="text-xs mt-2 text-gray-400">请确认角色已勾选「系统管理」→「操作日志」二级权限，然后刷新重试</p></div></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm"><i className="ri-history-line text-2xl mb-2 block text-gray-300"></i>暂无操作记录</td></tr>
              ) : (
                logs.map((log: any, idx: number) => {
                  const module = MODULE_MAP[log.action] || "其他";
                  const isCritical = CRITICAL_ACTIONS.has(log.action);
                  const actionLabel: Record<string, string> = {
                    login: "登录系统", create_client: "创建客户", edit_client: "编辑客户", delete_client: "删除客户",
                    progress_update: "进度更新", assign_advisor: "分配顾问", create_employee: "创建员工", update_employee: "编辑员工",
                    delete_employee: "删除员工", create_role: "创建角色", update_role: "编辑角色", delete_role: "删除角色",
                    send_notification: "发送通知", upload_file: "文件上传", delete_file: "文件删除",
                    change_password: "更改密码",
                    create_scheduled_notification: "定时通知", update_scheduled_notification: "定时通知",
                    cancel_scheduled_notification: "定时通知", send_scheduled_now: "定时通知",
                  };
                  const detailText = (() => {
                    const d = log.details || {};
                    const clientId = (log.target_type === "client" || log.target_type === "progress") && log.target_id ? log.target_id : null;
                    const clientInfo = clientId ? logsClients[clientId] : null;
                    const storedClientName = d.client_name || "未知";
                    const storedClientPhone = d.phone || d.client_phone || "-";
                    const displayClientName = clientInfo?.name || storedClientName;
                    const displayClientPhone = clientInfo?.phone || storedClientPhone;
                    // create_client 优先显示 client_code
                    const displayIdentifier = log.action === "create_client"
                      ? (d.client_code || storedClientPhone)
                      : displayClientPhone;
                    const clientPrefixText = `「${displayClientName}」(${displayIdentifier}) `;
                    const stageId = d.stage_id || null;
                    const stageName = d.stage_name || (stageId ? logsStages[stageId] : "");
                    const stageNameEl = stageName ? <span key="stage" className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px]">{stageName}</span> : null;

                    if (log.action === "create_client") return <>{clientPrefixText}新建客户</>;
                    if (log.action === "edit_client") return <>{clientPrefixText}编辑字段：{Array.isArray(d.updated_fields) ? d.updated_fields.map((f: string) => CLIENT_FIELD_LABELS[f] || f).join("、") : "-"}</>;
                    if (log.action === "delete_client") return <>{clientPrefixText}删除客户</>;
                    if (log.action === "assign_advisor") return <>{clientPrefixText}分配顾问「{d.advisor_name || "未知"}」</>;

                    if (log.action === "progress_update") {
                      const parts: string[] = [];
                      if (d.fee_updated) parts.push("更新收入数据");
                      if (d.expense_updated) parts.push("更新支出数据");
                      if (d.notes_updated) parts.push("更新阶段备注");
                      if (d.new_status === "completed") parts.push("标记为「已完成」");
                      else if (d.new_status === "in_progress") parts.push("标记为「进行中」");
                      const detail = parts.length > 0 ? parts.join("，") : "进度更新";
                      return <>{clientPrefixText}{stageNameEl}{detail}</>;
                    }
                    if (log.action === "upload_file") {
                      let detail = "";
                      if (d.file_names && Array.isArray(d.file_names)) {
                        const bucketLabel = d.bucket === "crm-media" ? "上传影像" : "上传资料";
                        detail = `${bucketLabel}：${d.file_names.join("、")}`;
                      } else {
                        detail = `上传文件「${d.file_name || "未知"}」`;
                      }
                      return <>{clientPrefixText}{stageNameEl}{detail}</>;
                    }
                    if (log.action === "delete_file") {
                      const bucketLabel = d.bucket === "crm-media" ? "删除影像" : "删除资料";
                      return <>{clientPrefixText}{stageNameEl}{bucketLabel}「{d.file_name || "未知"}」</>;
                    }

                    if (log.action === "login") return <>登录后台管理系统</>;
                    if (log.action === "send_notification") return <>发送通知「{d.title || ""}」给{d.client_count || 0}位客户</>;
                    if (log.action === "create_scheduled_notification") {
                      const scheduledAt = d.scheduled_at ? new Date(d.scheduled_at).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                      return <>创建定时通知「{d.title || ""}」，将于{scheduledAt && <span className="text-[#c9a96e]"> {scheduledAt} </span>}自动发送</>;
                    }
                    if (log.action === "update_scheduled_notification") {
                      const title = d.title || "";
                      const newTime = d.scheduled_at ? new Date(d.scheduled_at).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                      if (title && newTime) return <>定时通知「{title}」发送时间修改为<span className="text-[#c9a96e]"> {newTime} </span>自动发送</>;
                      return <>修改定时通知发送时间：共{d.count || 0}条通知</>;
                    }
                    if (log.action === "cancel_scheduled_notification") {
                      const title = d.title || "";
                      return title ? <>定时通知「{title}」已取消</> : <>取消定时通知：共{d.count || 0}条通知已取消</>;
                    }
                    if (log.action === "send_scheduled_now") {
                      const title = d.title || "";
                      const count = d.client_count || d.sent_count || 0;
                      if (title) return <>定时通知「{title}」已发送给{count}位客户</>;
                      return <>定时通知已发送：共{count}条</>;
                    }
                    if (log.action === "create_employee") return <>创建员工「{d.name || "未知"}」</>;
                    if (log.action === "update_employee") {
                      const empName = (log.target_id && logsEmployeeMap[log.target_id]) || d.employee_name || d.name || "";
                      return empName ? <>编辑员工「{empName}」信息</> : <>编辑员工信息</>;
                    }
                    if (log.action === "delete_employee") {
                      const empName = (log.target_id && logsEmployeeMap[log.target_id]) || d.employee_name || d.name || "";
                      const transferCount = d.transferred_clients || 0;
                      const transferTo = d.transferred_to || "";
                      const clientList: Array<{ id: number; name: string; client_code: string }> = d.transferred_client_list || [];
                      if (transferCount > 0 && transferTo) {
                        return (
                          <>
                            {empName ? <>删除员工「{empName}」，</> : <>删除员工，</>}
                            共转移 {transferCount} 位客户至「{transferTo}」名下
                            {clientList.length > 0 && (
                              <span className="text-gray-400 ml-1">
                                （{clientList.map((c) => c.name).join("、")}）
                              </span>
                            )}
                          </>
                        );
                      }
                      return empName ? <>删除员工「{empName}」{transferCount > 0 ? `，转移 ${transferCount} 位客户` : ""}</> : <>删除员工</>;
                    }
                    if (log.action === "create_role") return <>创建角色「{d.role_name || d.name || "未知"}」</>;
                    if (log.action === "update_role") {
                      const roleName = (log.target_id && logsRoleMap[log.target_id]) || d.role_name || d.name || "";
                      return roleName ? <>编辑角色「{roleName}」权限</> : <>编辑角色权限</>;
                    }
                    if (log.action === "delete_role") {
                      const roleName = d.role_name || d.name || (log.target_id && logsRoleMap[log.target_id]) || "";
                      return roleName ? <>删除角色「{roleName}」</> : <>删除角色</>;
                    }
                    if (log.action === "change_password") {
                      const empName = (log.target_id && logsEmployeeMap[log.target_id]) || log.employee_name || "";
                      return empName ? <>{empName} 更改密码</> : <>更改密码</>;
                    }
                    return <>-</>;
                  })();
                  return (
                    <tr key={log.id} className={`transition-colors ${isCritical ? "bg-red-50/60 hover:bg-red-100/60 border-l-2 border-l-red-400" : "hover:bg-gray-50"}`}>
                      <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">{logsPage * logsPageSize + idx + 1}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-sm font-medium text-[#1a3a5c]">{log.employee_name}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${isCritical ? "bg-red-100 text-red-700 font-semibold" : "bg-gray-100 text-gray-600"}`}>{module}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs min-w-[200px]">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded-full mr-1.5 ${isCritical ? "bg-red-100 text-red-700 font-semibold" : "bg-gray-50 text-gray-500"}`}>{actionLabel[log.action] || log.action}</span>
                        {isCritical && <i className="ri-alert-line text-red-500 mr-1 text-[11px]" title="高风险操作"></i>}
                        {detailText}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {logsCount > logsPageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">共 {logsCount} 条 · 第 {logsPage + 1} / {Math.ceil(logsCount / logsPageSize)} 页</p>
            <div className="flex gap-2">
              <button onClick={() => setLogsPage((p) => Math.max(0, p - 1))} disabled={logsPage === 0} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-left-s-line"></i></button>
              <button onClick={() => setLogsPage((p) => p + 1)} disabled={(logsPage + 1) * logsPageSize >= logsCount} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-right-s-line"></i></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}